import { CacheRule, Fetcher, FetcherParams } from '@dynamic/ui';

import { axios } from '#/client/axios';
import { FeedResponse } from '#/client/models';
import { changeUrlParams } from '#/service/utils';
import { historyActions } from '#/store/history/history.store';
import { translationStore } from '#/store/translation/translation.store';

let aborter = new AbortController();

export const DEFAULT_PAGE_SIZE = 20;

const EMPTY_FEED_RESPONSE: FeedResponse = {
  data: [],
};

const DONT_CACHE = [
  '/api/core/user/watch-history',
  '/api/core/user/favorites',
  '/api/core/recommendation/user',
];

const cache: { [key: string]: FeedResponse } = {};

/**
 * Fetch a generic feed by passing the Core API URL coming from DynUi to the server to call it. Supports in-memory caching
 * @param feedUrl {string} Core API feed URL evaluated from the `feed` DynUi property on the component.
 * @param page {number} Page number. 1-based.
 * @param pageSize {number} Page size.
 * @returns {Promise<FeedResponse>} Promise of the response.
 */
export const fetchFeed = async (
  feedUrl: string,
  page: number = 1,
  pageSize: number = DEFAULT_PAGE_SIZE
): Promise<FeedResponse> => {
  const locale = translationStore.locale;
  const key = `${feedUrl}:${page}:${pageSize}:${locale}`;
  const cachedResponse = cache[key];

  if (cachedResponse) {
    // we have to do this every time, even if the FeedResponse itself is cached, because the history info might have changed.
    return Promise.resolve({
      ...cachedResponse,
      data: historyActions.cloneWithWatchedObjects(cachedResponse.data),
    });
  }

  // `feedUrl` contains the initial page and pageSize params that we have to overwrite with the current arguments.
  const pageFeed = changeUrlParams(feedUrl, { page, pageSize });

  return axios
    .get<FeedResponse>(pageFeed, { signal: aborter.signal })
    .then((res) => {
      // a falsy response is not an error, falling back to an empty response
      const feedResponse = res?.data ?? EMPTY_FEED_RESPONSE;

      const isCacheable = !DONT_CACHE.find((pattern) =>
        feedUrl.includes(pattern)
      );

      /* We can assign cache rules with a context subject we'd like to watch if that value changes.
       * Currently this is used in dynamicUI to re-fetch content on currently viewed page if value changes.
       * This is phase 1 implementation and should be discussed in case we need more advanced logic in the future.
       *
       * What this means right now is that, if isHistoryFetched changes its value (see PageController.tsx),
       * we want to update context, watch for this change in dynamicUI (see Slider or Grid components in dynUI)
       * and act on that change (for now, we re-fetch the content). Different parameters can be watched depending
       * on if response is cacheable or not. **/
      const cacheRules: CacheRule[] = isCacheable
        ? [{ subject: 'isHistoryFetched' }]
        : [{ subject: 'user.isLoggedIn' }, { subject: 'lastUpdated' }];

      if (isCacheable) {
        cache[key] = { ...feedResponse, cacheRules };
      }

      return {
        ...feedResponse,
        cacheRules,
        data: historyActions.cloneWithWatchedObjects(feedResponse.data),
      };
    });
};

/** Fetcher function to be passed to DynUi via `setFetcher`. */
export const dynuiFetcher: Fetcher = async ({
  feedUrl,
  opts: { start, size },
}: FetcherParams) => {
  const page = Math.round(start / size + 1);

  if (!feedUrl) {
    throw new Error('Feed URL is empty or missing');
  }

  return fetchFeed(feedUrl, page, size).catch((err: Error) => {
    console.error('Container fetch failed', {
      feedUrl,
      error: err.message,
    });
    throw err;
  });
};

/** This method cancels active API requests for dynamic UI stuff */
export function cancelOldRequests() {
  // Sending abort to each requests which listens to the `aborter.signal`
  aborter.abort();

  // AbortController needs to be recreated once aborted.
  aborter = new AbortController();
}
